import { useCallback, useEffect, useMemo, useState } from "react";

import { ErrorBoundary, Suspense } from "@suspensive/react";
import { Calculator } from "lucide-react";
import { SuspenseQuery } from "@suspensive/react-query";
import { QueryErrorResetBoundary, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ContestArchiveDownload } from "@/components/contest-submissions/contest-archive-download";
import { ContestSubmissionBulkBar } from "@/components/contest-submissions/contest-submission-bulk-bar";
import { ContestSubmissionFilters } from "@/components/contest-submissions/contest-submission-filters";
import { ContestSubmissionStats } from "@/components/contest-submissions/contest-submission-stats";
import { ContestSubmissionTable } from "@/components/contest-submissions/contest-submission-table";
import { SectionErrorFallback } from "@/components/error-boundary/error-fallback";
import { TableLoadingFallback } from "@/components/suspense/loading-fallback";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  contestSubmissionDetailQueryOptions,
  contestSubmissionsQueryOptions,
  useRecountCharCount,
} from "@/hooks/use-contest-submissions";
import type { UsePaginationReturn } from "@/hooks/use-pagination";
import type {
  ContestScreeningResult,
  ContestSubmissionListItem,
  ContestSubmissionsFilters,
} from "@/types/contest-submission";

const PAGE_LIMIT = 20;
const SCREENING_VALUES = new Set(["PENDING", "PASS", "FAIL"]);

/**
 * 목록 상태(검색/심사 필터/페이지)를 URL search params 로 관리한다 —
 * 상세 페이지에서 뒤로가기 시 목록 위치가 그대로 복원되게 (드로어 시절엔 불필요했음).
 */
export default function ContestSubmissionsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get("q") ?? undefined;
  const rawScreening = searchParams.get("screening") ?? "";
  const screeningResult = SCREENING_VALUES.has(rawScreening)
    ? (rawScreening as ContestScreeningResult)
    : undefined;
  const page = Math.max(1, Number(searchParams.get("page")) || 1);

  const filters: ContestSubmissionsFilters = useMemo(
    () => ({ search, screeningResult }),
    [search, screeningResult],
  );

  const setFilters = useCallback(
    (next: ContestSubmissionsFilters) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (next.search) params.set("q", next.search);
        else params.delete("q");
        if (next.screeningResult) params.set("screening", next.screeningResult);
        else params.delete("screening");
        params.delete("page"); // 필터 변경 시 1페이지로
        return params;
      });
    },
    [setSearchParams],
  );

  const setPage = useCallback(
    (nextPage: number) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (nextPage > 1) params.set("page", String(nextPage));
        else params.delete("page");
        return params;
      });
    },
    [setSearchParams],
  );

  // 기존 테이블/페이지네이션 컴포넌트 인터페이스(UsePaginationReturn)에 맞춘 어댑터.
  const pagination: UsePaginationReturn = useMemo(
    () => ({
      page,
      limit: PAGE_LIMIT,
      setPage,
      setLimit: () => undefined,
      nextPage: () => setPage(page + 1),
      prevPage: () => setPage(page - 1),
      resetPagination: () => setPage(1),
      canGoNext: true,
      canGoPrev: page > 1,
    }),
    [page, setPage],
  );

  // 일괄 판정용 체크박스 선택. 페이지/필터가 바뀌면 초기화한다 —
  // effect 대신 렌더 중 상태 조정 패턴(React 공식 "adjusting state when props change").
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const listKey = `${search ?? ""}|${screeningResult ?? ""}|${page}`;
  const [prevListKey, setPrevListKey] = useState(listKey);
  if (prevListKey !== listKey) {
    setPrevListKey(listKey);
    setSelectedIds(new Set());
  }

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleToggleSelectAll = useCallback((pageIds: string[]) => {
    setSelectedIds((prev) => {
      const allSelected = pageIds.every((id) => prev.has(id));
      return allSelected ? new Set<string>() : new Set(pageIds);
    });
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // 다음 페이지 prefetch — 표 탐색 시 깜빡임 최소화.
  useEffect(
    function prefetchNextPage() {
      queryClient.prefetchQuery(
        contestSubmissionsQueryOptions(filters, {
          page: page + 1,
          limit: PAGE_LIMIT,
        }),
      );
    },
    [queryClient, filters, page],
  );

  const handleOpen = useCallback(
    (item: ContestSubmissionListItem) => {
      navigate(`/contest-submissions/${item._id}`);
    },
    [navigate],
  );

  // 글자수 일괄 계산 — 누락/실패/집계중 잔존 건 전체를 서버 큐에 재등록.
  // 집계중 건이 생기면 목록 폴링(refetchInterval)이 완료까지 자동 갱신한다.
  const recountAll = useRecountCharCount();
  const handleRecountAll = useCallback(() => {
    if (
      !confirm(
        "글자수가 없거나 실패한 제출물 전체를 일괄 계산합니다. 진행할까요?\n(완료되는 대로 목록이 자동 갱신됩니다)",
      )
    ) {
      return;
    }
    recountAll.mutate(undefined);
  }, [recountAll]);

  // 행 hover 시 상세 prefetch — 상세 진입 체감 속도 개선.
  const handlePrefetch = useCallback(
    (item: ContestSubmissionListItem) => {
      queryClient.prefetchQuery(contestSubmissionDetailQueryOptions(item._id));
    },
    [queryClient],
  );

  const queryOptions = useMemo(
    () =>
      contestSubmissionsQueryOptions(filters, { page, limit: PAGE_LIMIT }),
    [filters, page],
  );

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">공모전 응모 이력</h1>
        <p className="text-muted-foreground leading-normal">
          노벨라 오컬트 스토리 공모전 〈경계에 선 존재들〉 — 인앱 제출 이력을 조회합니다.
          (자동저장 draft 는 제외)
        </p>
      </div>

      <ContestSubmissionStats />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>제출 목록</CardTitle>
          <div className="flex items-center gap-2">
            <ContestArchiveDownload />
            <Button
              variant="outline"
              size="sm"
              onClick={handleRecountAll}
              disabled={recountAll.isPending}
            >
              <Calculator className="mr-1.5 h-3.5 w-3.5" />
              {recountAll.isPending ? "요청 중…" : "글자수 일괄 계산"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 px-4 md:px-6">
          <ContestSubmissionFilters
            filters={filters}
            onFiltersChange={setFilters}
          />

          <ContestSubmissionBulkBar
            selectedIds={[...selectedIds]}
            onClear={handleClearSelection}
          />

          <QueryErrorResetBoundary>
            {({ reset }) => (
              <ErrorBoundary onReset={reset} fallback={SectionErrorFallback}>
                <Suspense fallback={<TableLoadingFallback />}>
                  <SuspenseQuery {...queryOptions}>
                    {({ data }) => (
                      <ContestSubmissionTable
                        data={data.items}
                        meta={data.meta}
                        pagination={pagination}
                        onOpen={handleOpen}
                        onPrefetch={handlePrefetch}
                        selectedIds={selectedIds}
                        onToggleSelect={handleToggleSelect}
                        onToggleSelectAll={handleToggleSelectAll}
                      />
                    )}
                  </SuspenseQuery>
                </Suspense>
              </ErrorBoundary>
            )}
          </QueryErrorResetBoundary>
        </CardContent>
      </Card>
    </div>
  );
}
